import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentProps, forwardRef } from "react";

import { CAMPAIGN_TYPES } from "common/constants";
import type { CampaignType } from "common/types";

const campaignTypeIconVariants = cva(
  "inline-flex shrink-0 items-center justify-center transition-[background-color]",
  {
    variants: {
      size: {
        xs: "size-6 rounded-lg [&_svg]:size-3",
        sm: "size-8 rounded-xl [&_svg]:size-4",
        md: "size-10 rounded-2xl [&_svg]:size-5",
        lg: "size-12 rounded-3xl [&_svg]:size-6",
      },
      variant: { default: "", drip: "" },
      state: {
        default: "",
        disabled: "bg-black-100 opacity-60 [&_svg]:fill-black-950",
        selected: "bg-white",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
      state: "default",
    },
    compoundVariants: [
      {
        variant: "default",
        state: "default",
        class: "bg-black-100 group-hover:bg-white",
      },
      {
        variant: "drip",
        state: ["default", "selected"],
        class: "[&_svg]:fill-blue-700 ",
      },
      {
        variant: "drip",
        state: "default",
        class: "bg-blue-100 group-hover:bg-white",
      },
    ],
  },
);

type CampaignTypeIconProps = Omit<ComponentProps<"span">, "children"> &
  VariantProps<typeof campaignTypeIconVariants> & {
    type: CampaignType;
  };

const CampaignTypeIcon = forwardRef<HTMLSpanElement, CampaignTypeIconProps>(
  ({ className, size, type, variant, state, ...props }, ref) => {
    const { icon: Icon } = CAMPAIGN_TYPES[type];

    return (
      <span
        ref={ref}
        className={campaignTypeIconVariants({
          size,
          className,
          variant,
          state,
        })}
        {...props}
      >
        <Icon weight="duotone" />
      </span>
    );
  },
);
CampaignTypeIcon.displayName = "CampaignTypeIcon";

export default CampaignTypeIcon;
